<template>
  <div style="margin-bottom: 1em;">
    <template v-if="!hasQuery">
      <div class="form-control-space" style="color: #737373; font-style: italic;
    font-weight: 300;
    font-size: 14px; margin-top: 5px;
    margin-bottom: 10px;">{{
          inviteMessage ? inviteMessage : $t('cap-swift-selector.inviteMessage')
        }}
      </div>
    </template>
    <template v-if="hasQuery">
      <template v-if="hasResults">
        <i18n tag="div" path="cap-swift-selector.foundresults">
          <template v-slot:query><strong>&laquo;{{ searchQuery }}&raquo;</strong></template>
          :
        </i18n>
        <dropdown-key-value :options="selectorOptions"
                            :allow-not-found="true"
                            :placeholder="placeholder ? placeholder : $t('cap-swift-selector.placeholder')"
                            :value="query"
                            @input="onChange"></dropdown-key-value>
      </template>
      <div v-if="hasNoResults" class="form-control-space">
        <i18n tag="span" path="cap-swift-selector.notfound">
          <template v-slot:query><strong>&laquo;{{ searchQuery }}&raquo;</strong></template>
        </i18n>
      </div>
      <div v-if="inProgress">
        <i18n tag="span" path="cap-swift-selector.searching">
          <template v-slot:query><strong>&laquo;{{ searchQuery }}&raquo;</strong></template>
        </i18n>
      </div>
    </template>

  </div>
</template>

<script lang="ts">
import DropdownKeyValue from "@/components/forms/dropdown/DropdownKeyValue.vue";
import debounce from "@/polyfill/Debounce";
import {computed, defineComponent, onMounted, reactive, ref, triggerRef, watch} from "vue";
import {WebFormModel} from "capitalist-shared/src/core/model";
import axios from "axios";

class SwiftSearchModel extends WebFormModel {
  search(q: string) {
    this.errors.clear();
    return axios.post('/webapi/wires/swift', {q}).then((response) => {
      return response.data;
    }).catch((error) => {
      this.checkAxiosError(error);
      throw error;
    });
  }
}

export default defineComponent({
  name: 'SwiftSelector',
  components: {
    'dropdown-key-value': DropdownKeyValue
  },
  props: {
    query: {default: '', type: String},
    placeholder: {default: null},
    inviteMessage: {default: null},
  },
  emits: ['bank-selected'],
  setup(props, context) {
    const selectedCode = ref('');
    const searchResults = ref([]);
    const searchQuery = ref('');
    const p = ref(null);
    const dirty = ref(false);
    const searchModel = reactive(new SwiftSearchModel());

    const searching = (query) => {
      if (searchQuery.value == query) {
        searchResults.value.splice(0, searchResults.value.length);
        searchModel.search(query).then((result) => {
          if (searchQuery.value == query) {
            searchResults.value.splice(0, searchResults.value.length, ...result.items);
            dirty.value = false;
          }
        }).catch((error) => {
          if (searchQuery.value == query) {
            dirty.value = false;
          }
        });
      }

    };
    const searchSwift: any = debounce(searching as any, 500);

    const onChange = (value) => {
      const idx = searchResults.value.findIndex((bank) => {
        return (bank.code.toUpperCase() == value.toUpperCase());
      });
      if (idx > -1) {
        context.emit('bank-selected', searchResults.value[idx]);
      }
    }

    const onValueUpdate = (newVal) => {
      if (newVal != searchQuery.value) {
        const query = newVal ? String(newVal).trim() : '';

        if (query.length == 0) {
          searchResults.value.splice(0, searchResults.value.length);
          searchQuery.value = '';
          return;
        }

        if ((searchResults.value.length > 0)
          && query
          && (query.length > 1)
          && (query == props.query)) {
          const qc = query.toUpperCase();
          const idx = searchResults.value.findIndex((bank) => {
            return (bank.code.toUpperCase() == qc.toUpperCase());
          });
          if (idx !== -1) {
            context.emit('bank-selected', searchResults.value[idx]);
            dirty.value = false;
            return;
          }
        }
        dirty.value = true;
        searchQuery.value = query;
        searchSwift(query);
      }
    }

    onMounted(() => {
      onValueUpdate(props.query ? props.query as string : '');
    });

    watch(() => props.query, onValueUpdate);

    const hasQuery = computed(() => {
      return props.query && (String(props.query).length > 1);
    });

    const waitingForQuery = computed(() => {
      return !hasQuery.value;
    });

    const inProgress = computed(() => {
      return dirty.value && !waitingForQuery.value;
    });



    const hasResults = computed(() => {
      return !dirty.value && hasQuery.value && searchResults.value && (searchResults.value.length > 0);
    });

    const hasNoResults = computed(() => {
      return !dirty.value && hasQuery.value && searchResults.value && (searchResults.value.length == 0);
    });

    const selectorOptions = computed(() => {
      const result = {};
      searchResults.value.forEach((bank) => {
        result[bank.code] = `${bank.code} / ${bank.bankName} / ${bank.country} / ${bank.city}`;
      });
      return result;
    });

    return {
      selectorOptions,
      hasNoResults,
      hasResults,
      inProgress,
      waitingForQuery,
      hasQuery,
      onChange,
      searchModel,
      selectedCode,
      searchResults,
      searchQuery,
      p,
      dirty,
    }
  }
});

</script>

<style scoped>

</style>

<i18n>
{
  "en": {
    "cap-swift-selector": {
      "placeholder": "Select bank",
      "inviteMessage": "Start typing SWIFT code to automatically search for bank.",
      "notfound": "No banks were found for the string {query}. But that's okay, maybe our code base is just outdated. Check the code for typos and feel free to submit payment.",
      "searching": "Looking for banks by SWIFT code {query}...",
      "foundresults": "Found banks by SWIFT code {query}"
    }
  },
  "ru": {
    "cap-swift-selector": {
      "placeholder": "Выберите банк",
      "inviteMessage": "Начните вводить SWIFT-код для автоматического поиска банка.",
      "notfound": "Банки по строке {query} не найдены. Но в этом ничего страшного, возможно, наша база кодов просто устарела. Проверьте код на опечатки и спокойно отправляйте.",
      "searching": "Поиск банков по SWIFT-коду {query}...",
      "foundresults": "По строке {query} найдены банки"
    }
  }
}
</i18n>
